import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import {
  localImages,
  stringsList,
} from '../../constants/app-config';
import { MatDialog } from '@angular/material/dialog';
import { AddTemplateDialogComponent } from '../dialogs/add-template-dialog/add-template-dialog.component';
import { TemplateModel } from '../models/template.model';
import { TemplateService, TemplateType } from '../services/template.service';
import { TranslateService } from '@ngx-translate/core';
import { SmallDialogComponent } from '../../atoms/small-dialog/small-dialog.component';
import { ActivatedRoute, Router } from '@angular/router';
import { NewTemplateModel } from '../models/new-template.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { fromEvent, of } from "rxjs";
import { debounceTime, map, switchAll, tap } from "rxjs/operators";
import { TF } from 'src/app/API.service';
import { BANK_STATEMENT_FIELD_LABELS } from '../../constants/bank-statement-fields';

@Component({
  selector: 'app-templates',
  templateUrl: './templates.component.html',
  styleUrls: ['./templates.component.css'],
})
export class TemplatesComponent implements OnInit {
  stringsList = stringsList;
  searchTemplateInput: string = '';
  templates: any[] = [];
  localImages = localImages;
  loading = false;
  isSearchFilterOn = false;
  create_template: boolean = false;
  showIcon = false;
  showClearIcon = false;
  isInputFocused = false;
  isHovered: boolean = false;
  private nextToken: string | undefined
  isAdditionalDataBeingFetched: boolean = false;
  userID!: string | null;
  private savedScrollPosition = 0;
  @ViewChild('scrollableContainer') scrollableContainer!: ElementRef<HTMLElement>;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialog: MatDialog,
    private templateService: TemplateService,
    private translate: TranslateService,
    private snackbar: MatSnackBar,
    private input: ElementRef<HTMLInputElement>
  ) {
    const obs = fromEvent(this.input.nativeElement, 'keyup')
      .pipe(
        map((e: any) => {
          return e.target.value
        }),
        debounceTime(250),
        tap(() => {
          this.isSearchFilterOn = true
        }),
        map((searchInput: string) => {
          return of(searchInput?.toLowerCase())
        }),
        switchAll()
      )
    obs.subscribe(async data => {
      if (data.length) {
        await this.getTemplates(data).catch(err => console.error(err))
      }
      else {
        await this.getTemplates(data).catch(err => console.error(err))
        this.isSearchFilterOn = false
      }
    })
  }

  ngOnInit(): void {
    this.getTemplates().catch((err) => {
      console.log(err);
      this.loading = false;
    });

    this.create_template =
      this.route.snapshot.queryParams.create_template == 'true';
    if (this.create_template) {
      this.addNewTemplate();
    }
  }

  toggleIcon() {
    this.showClearIcon = !!this.searchTemplateInput;
  }

  clearSearch(): void {
    this.searchTemplateInput = '';
    this.showClearIcon = false;
    this.getTemplates().catch(err => console.error(err))
    this.isSearchFilterOn = false
  }

  async getTemplates(filter?: string) {
    const cachedTemplates = JSON.parse(localStorage.getItem('templates') || '[]');

    if (!cachedTemplates.length) {
      this.loading = true;
    }
    this.templates = this.sortTemplates(cachedTemplates);

    // Send a request to fetch the latest templates
    this.userID = localStorage.getItem('userID');
    const response =
      await this.templateService
        .ListTemplatesByClientID(
          this.userID as string,
          undefined,
          filter ? { searchKeyword: { contains: filter } } : undefined,
          filter ? 10000 : 100
        );
    if (response.nextToken) {
      this.nextToken = response.nextToken
    } else this.nextToken = undefined
    const newTemplates = response.items.map((el: any) => {
      return {
        id: el.id,
        _name: el.name,
        _description: el.description ? el.description : '',
        _updatedAt: el.updatedAt,
      };
    });

    // Check if the new templates are different from the cached templates
    const templatesChanged = this.areTemplatesDifferent(cachedTemplates, newTemplates);

    // If the templates have changed, update the local storage and the component's templates array
    if (templatesChanged) {
      localStorage.setItem('templates', JSON.stringify(newTemplates));
      this.templates = this.sortTemplates(newTemplates);
    } else {
      // If the templates haven't changed, use the cached templates
      this.templates = this.sortTemplates(cachedTemplates);
    }

    this.loading = false;
  }

  async onScroll(e: any) {
    if (this.isSearchFilterOn) {
      return
    }
    const scrollableContainer = this.scrollableContainer.nativeElement;
    const containerHeight = scrollableContainer.clientHeight;
    const scrollHeight = scrollableContainer.scrollHeight;
    const scrollTop = scrollableContainer.scrollTop;

    // Calculate the remaining distance to the bottom
    const remainToScrollToBottom = scrollHeight - (scrollTop + containerHeight);

    this.savedScrollPosition = scrollTop;

    // Define a threshold percentage (e.g., 90%) for triggering data loading
    const thresholdPercentage = 0.9;

    if (remainToScrollToBottom < containerHeight * thresholdPercentage && !this.isAdditionalDataBeingFetched) {
      this.isAdditionalDataBeingFetched = true;

      if (!this.nextToken) return;

      const response = await this.templateService.ListTemplatesByClientID(
        this.userID as string,
        undefined,
        undefined,
        100,
        this.nextToken
      );

      if (response.nextToken) {
        this.nextToken = response.nextToken;
      } else {
        this.nextToken = undefined;
      }

      const newTemplates = response.items.map((el: any) => {
        return {
          id: el.id,
          _name: el.name,
          _description: el.description ? el.description : '',
          _updatedAt: el.updatedAt,
        };
      });

      this.templates = this.templates.concat(newTemplates);
      this.isAdditionalDataBeingFetched = false;
    }
  }



  areTemplatesDifferent(templates1: any[], templates2: any[]): boolean {
    // Check if the lengths of the arrays are different
    if (templates1.length !== templates2.length) {
      return true;
    }

    // Compare individual elements to check if anything inside is changed
    for (let i = 0; i < templates1.length; i++) {
      if (
        templates1[i].id !== templates2[i].id ||
        templates1[i]._name !== templates2[i]._name ||
        templates1[i]._description !== templates2[i]._description ||
        templates1[i]._updatedAt !== templates2[i]._updatedAt
      ) {
        return true;
      }
    }

    return false;
  }



  sortTemplates(templates: any[]) {
    return templates.sort((a: any, b: any) => {
      const updatedAtA = new Date(a._updatedAt).getTime();
      const updatedAtB = new Date(b._updatedAt).getTime();
      return updatedAtB - updatedAtA;
    });
  }

  mapLabels(response: any, el: string) {
    if (response.data.titles[el] === '') {
      if (el === 'taxBase') {
        response.data.titles[el] = 'Tax base';
      } else if (el === 'ID') {
        response.data.titles[el] = 'ID number';
      } else if (el === 'vat') {
        response.data.titles[el] = 'VAT';
      } else if (el === 'total') {
        response.data.titles[el] = 'Total';
      } else if (el === 'code') {
        response.data.titles[el] = 'Code';
      } else if (el === 'dueDate') {
        response.data.titles[el] = 'Due date';
      } else if (el === 'documentName') {
        response.data.titles[el] = 'Document';
      } else if (el === 'companyName') {
        response.data.titles[el] = 'Company';
      } else if (el === 'invoiceDate') {
        response.data.titles[el] = 'Invoice date';
      } else if (el === 'invoiceNumber') {
        response.data.titles[el] = 'Invoice number';
      } else if (el === 'vatID') {
        response.data.titles[el] = 'VATID';
      } else if (el === 'deductibleTaxBase') {
        response.data.titles[el] = 'Deductible tax base';
      } else if (el === 'nonDeductibleTaxBase') {
        response.data.titles[el] = 'Non-deductible tax base';
      } else if (el === 'deductibleVAT') {
        response.data.titles[el] = 'Deductible VAT';
      } else if (el === 'nonDeductibleVAT') {
        response.data.titles[el] = 'Non-deductible VAT';
      } else if (el === 'documentIndex') {
        response.data.titles[el] = 'KUF No.';
      } else if (el === 'documentURL') {
        response.data.titles[el] = 'Document source';
      } else if (el === 'productCode') {
        response.data.titles[el] = 'Product Code';
      } else if (el === 'barcode') {
        response.data.titles[el] = 'Barcode';
      } else if (el === 'number') {
        response.data.titles[el] = 'Number';
      } else if (el === 'productName') {
        response.data.titles[el] = 'Product Name';
      } else if (el === 'unit') {
        response.data.titles[el] = 'Unit';
      } else if (el === 'quantity') {
        response.data.titles[el] = 'Quantity';
      } else if (el === 'priceWithTax') {
        response.data.titles[el] = 'Price with Tax';
      } else if (el === 'priceWithoutTax') {
        response.data.titles[el] = 'Price without Tax';
      } else if (el === 'discountPercentage') {
        response.data.titles[el] = 'Discount %';
      } else if (el === 'discountAmount') {
        response.data.titles[el] = 'Discount Amount';
      } else if (el === 'taxPercentage') {
        response.data.titles[el] = 'Tax %';
      } else if (el === 'taxAmount') {
        response.data.titles[el] = 'Tax Amount';
      } else if (el === 'retailPrice') {
        response.data.titles[el] = 'Retail Price';
      } else if (el === 'totalPrice') {
        response.data.titles[el] = 'Total Price';
      } else if (el === 'totalPriceWithoutTax') {
        response.data.titles[el] = 'Total Price without Tax';
      } else if (el === 'totalPriceWithoutTaxBeforeDiscount') {
        response.data.titles[el] = 'Total Price without Tax before Discount';
      } else if (el === 'incomeExpenseDate') {
        response.data.titles[el] = 'Datum prihoda/rashoda';
      } else if (el === 'viaBankAccount') {
        response.data.titles[el] = 'Preko bankovnog računa';
      } else if (el === 'inGoodsMaterials') {
        response.data.titles[el] = 'U robi / materijalu';
      } else if (el === 'grossEmployeeWages') {
        response.data.titles[el] = 'Bruto plaće zaposlenika';
      } else if (el === 'paidEntrepreneurContributions') {
        response.data.titles[el] = 'Plaćeni doprinosi poduzetnika';
      } else if (el === 'otherExpenses') {
        response.data.titles[el] = 'Ostalo';
      } else if (el === 'documentDescription') {
        response.data.titles[el] = 'Opis dokumenta';
      } else if (el === 'konto') {
        response.data.titles[el] = 'Konto';
      } else if (el === 'schema') {
        response.data.titles[el] = 'Schema';
      } else if (el === 'buyerName') {
        response.data.titles[el] = 'Buyer Name';
      } else if (el === 'buyerID') {
        response.data.titles[el] = 'Buyer ID';
      } else if (el === 'buyerVATID') {
        response.data.titles[el] = 'Buyer VAT ID';
      } else if (BANK_STATEMENT_FIELD_LABELS[el]) {
        response.data.titles[el] = BANK_STATEMENT_FIELD_LABELS[el];
      } else if (!response.data.titles[el]) {
        response.data.titles[el] = '';
      } else {
        response.data.titles[el] = el;
      }
    }
    return response.data.titles[el];
  }

  addNewTemplate() {
    const dialogRef = this.dialog.open(
      AddTemplateDialogComponent,
      this.openDialogProperties()
    );

    dialogRef.afterClosed().subscribe((response) => {
      if (response?.action === stringsList.addAction) {
        const template = this.getTemplateMutationObject(response);

        this.templateService
          .CreateTemplate(template)
          .then(async (item) => {
            this.templates.unshift(this.mapNewTemplate(item));
            this.snackbar.open('The template successfully added', 'Dismiss', {
              duration: 5000,
              panelClass: ['white-snackbar'],
            });
            if (this.create_template) {
              this.router.navigate(['/invoices'], {
                queryParams: {
                  id: this.route.snapshot.queryParams.id,
                  download_template: true,
                },
              });
            }
          })
          .catch((err) => console.log(err));
      }
    });
  }

  private getTemplateMutationObject(response: any) {
    const templateName = response.data.titles.name;
    const templateDescription = response.data.titles.description;
    const form: { key: string; label: string; isChecked: boolean }[] =
      response.data.attributeValues
        .filter((att: string) => att !== 'name' && att !== 'description')
        .map((el: any) => {
          return {
            key: el,
            label: this.mapLabels(response, el),
            isChecked: response.data.isToggleChecked[el],
          };
        });
    const orderedObject: any = {};
    if (response.data.isDocumentIndexChecked) {
      orderedObject['documentIndex'] = response.data.documentIndexValue.value ? response.data.documentIndexValue.value : response.data.documentIndexValue.key
    }
    form.forEach(
      ({ key, label, isChecked }) =>
        isChecked && Object.assign(orderedObject, { [key]: label })
    );
    let userId = localStorage.getItem('userID');
    let productsEnabled = response.data.templateType === TemplateType.GOODS ? TF.TRUE : TF.FALSE;

    let temp: NewTemplateModel = {
      name: templateName,
      description: templateDescription,
      clientID: userId,
      template: JSON.stringify(orderedObject),
      productsEnabled: productsEnabled,
      isFixedValueEnabled: JSON.stringify(response.data.isFixedValueEnabled),
      fixedValues: JSON.stringify(response.data.fixedValues)
    };
    return temp;
  }

  mapNewTemplate(newTemp: any) {
    return {
      _name: newTemp.name,
      _description: newTemp.description ? newTemp.description : '',
      id: newTemp.id,
    };
  }

  openDialogProperties(template?: any) {
    return {
      width: '100%',
      data: template,
      panelClass: ['bottom-to-top', 'custom-dialog'],
    };
  }

  openSmallDialogProperties(data: any) {
    return {
      width: '460px',
      data: data,
    };
  }

  deleteTemplate(template: TemplateModel) {
    let dialogData: any = {
      heading: 'Delete template?',
      contentPartOne: 'Are you sure you want to delete template: ',
      contentPartTwo:
        'You can’t undo this action.',
      contentFile: template._name,
      action: 'Delete template',
    };
    const dialogRef = this.dialog.open(
      SmallDialogComponent,
      this.openSmallDialogProperties(dialogData)
    );
    dialogRef.afterClosed().subscribe((response) => {
      if (response?.action === stringsList.doAction) {
        this.templates = this.templates.filter(
          (deletedFilter) => deletedFilter.id !== template.id
        );
        this.templateService
          .DeleteTemplate({ id: template.id! })
          .then((res) => {
            this.snackbar.open('The template has been deleted', '', {
              duration: 5000,
              panelClass: ['white-snackbar'],
            });
          })
          .catch((err) => console.log(err));
      }
    });
  }

  editTemplate(template: TemplateModel) {
    this.templateService
      .GetTemplate(template.id!)
      .then((data: any) => {
        data.template = JSON.parse(data.template);
        const keysArray = Object.keys(data.template);
        const keysAsStrings = keysArray.map((key) => String(key));

        let fullTemplate: any = {
          name: data.name.trim(),
          description: data.description.trim(),
          isFixedValueEnabled: data.isFixedValueEnabled ? JSON.parse(data.isFixedValueEnabled) : { ID: false, vatID: false },
          fixedValues: data.fixedValues ? JSON.parse(data.fixedValues) : { ID: '', vatID: '' }
        };

        for (const [key, value] of Object.entries(data.template)) {
          fullTemplate[key] = typeof value === 'string' ? value.trim() : value;
        }

        const templateType =
          data.productsEnabled === 'TRUE' ? TemplateType.GOODS : TemplateType.REGULAR;

        const dialogRef = this.dialog.open(
          AddTemplateDialogComponent,
          this.openDialogProperties({
            template: fullTemplate,
            keysAsStrings: keysAsStrings,
            templateType: templateType,
          })
        );

        dialogRef.afterClosed().subscribe((response) => {
          if (response?.action === stringsList.saveChanges) {
            const templateData: any = this.getTemplateMutationObject(response);
            templateData.id = template.id;
            this.templateService
              .UpdateTemplate(templateData)
              .then((data: any) => {
                this.snackbar.open('Changes successfully saved', 'Dismiss', {
                  duration: 5000,
                  panelClass: ['white-snackbar'],
                });
                this.templates = this.templates.map((el) => {
                  if (el.id === data.id) {
                    el._name = data?.name;
                    el._description = data?.description;
                  }
                  return el;
                });
              })
              .catch((err) => console.log(err));
          }
        });
      })
      .catch((err) => console.log(err));
  }

}
